import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveCcConnector,
  createCcConnector,
  listCcConnectors,
  updateCcConnector,
  type CcConnector,
  type CcConnectorCreate,
  type CcConnectorPatch,
} from '../api/ccConnectors.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

/**
 * #150/#151 — Sección "Contact Center" del admin del tenant.
 *
 * Five9 Digital usa auth ANÓNIMO cookieless: NO se requiere apiKey/password.
 * El admin sólo configura URLs + tenantName + campaign + interactionTaskKey.
 * En runtime, connectors-cc se autentica dinámicamente con
 *   POST {authBaseUrl}/appsvcs/rs/svc/auth/anon?cookieless=true
 *   body: { tenantName }
 * y cachea el tokenId 90s en zen-dev-cc-auth-sessions.
 */

interface FormValues {
  name: string;
  authBaseUrl: string;
  messagesBaseUrl: string;
  tenantName: string;
  campaign: string;
  interactionTaskKey: string;
  status: 'connected' | 'error';
}

const emptyValues: FormValues = {
  name: '',
  authBaseUrl: 'https://app.five9.com',
  messagesBaseUrl: 'https://app-atl.five9.com:443',
  tenantName: '',
  campaign: '',
  interactionTaskKey: '',
  status: 'connected',
};

export function CcConnectorsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<CcConnector | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<CcConnector | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['cc-connectors', id, { includeArchived }],
    queryFn: () => listCcConnectors(id, { includeArchived }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (target: CcConnector) => archiveCcConnector(id, target.id, target.version),
    onSuccess: () => {
      pushToast('success', 'Contact-center connector archivado');
      queryClient.invalidateQueries({ queryKey: ['cc-connectors', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => pushToast('error', toMsg(e)),
  });

  const connectors = data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Contact Center · Five9 Digital</h2>
          <p className="text-sm text-slate-500">
            Five9 Digital usa autenticación anónima — no se requiere apiKey. El admin sólo
            configura URLs + tenantName + campaign. Cada handoff hace login dinámico contra Five9
            con cache de token (TTL 90s).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Ver archivados
          </label>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            Nuevo connector
          </button>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && connectors.length === 0 && (
        <p className="text-slate-500">No hay connectors de contact-center configurados.</p>
      )}

      {connectors.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Auth URL</th>
                <th className="px-3 py-2">Tenant Five9</th>
                <th className="px-3 py-2">Campaign</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Versión</th>
                <th className="px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {connectors.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.authBaseUrl}</td>
                  <td className="px-3 py-2">{c.tenantName}</td>
                  <td className="px-3 py-2">{c.campaign}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={c.status} />
                    {c.deletedAt && <span className="ml-1 text-xs text-slate-500">(archivado)</span>}
                  </td>
                  <td className="px-3 py-2">{c.version}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={c.deletedAt !== null}
                        onClick={() => setEditing(c)}
                        className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        disabled={c.deletedAt !== null}
                        onClick={() => setConfirmTarget(c)}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Archivar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <CcConnectorModal
          tenantId={id}
          target={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['cc-connectors', id] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title="Archivar connector"
        description={
          confirmTarget
            ? `¿Archivar "${confirmTarget.name}"? Esto deshabilita el handoff a Five9 para este tenant.`
            : ''
        }
        confirmLabel="Archivar"
        cancelLabel="Cancelar"
        onConfirm={() => confirmTarget && archiveMutation.mutate(confirmTarget)}
        onCancel={() => setConfirmTarget(null)}
        loading={archiveMutation.isPending}
      />
    </div>
  );
}

interface CcConnectorModalProps {
  tenantId: string;
  target: CcConnector | null;
  onClose: () => void;
  onSaved: () => void;
}

function CcConnectorModal({ tenantId, target, onClose, onSaved }: CcConnectorModalProps): JSX.Element {
  const mode = target ? 'edit' : 'create';
  const pushToast = useUiStore((s) => s.pushToast);

  const defaultValues: FormValues = target
    ? {
        name: target.name,
        authBaseUrl: target.authBaseUrl,
        messagesBaseUrl: target.messagesBaseUrl ?? '',
        tenantName: target.tenantName,
        campaign: target.campaign,
        interactionTaskKey: target.interactionTaskKey,
        status: target.status,
      }
    : emptyValues;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => reset(defaultValues), [target?.id]);

  const createMutation = useMutation({
    mutationFn: (body: CcConnectorCreate) => createCcConnector(tenantId, body),
    onSuccess: () => {
      pushToast('success', 'Connector creado');
      onSaved();
    },
    onError: (e: unknown) => pushToast('error', toMsg(e)),
  });

  const updateMutation = useMutation({
    mutationFn: (patch: CcConnectorPatch) => updateCcConnector(tenantId, target!.id, patch),
    onSuccess: () => {
      pushToast('success', 'Connector actualizado');
      onSaved();
    },
    onError: (e: unknown) => pushToast('error', toMsg(e)),
  });

  const onSubmit = handleSubmit((values) => {
    if (mode === 'create') {
      createMutation.mutate({
        name: values.name,
        provider: 'five9_digital',
        authBaseUrl: values.authBaseUrl,
        ...(values.messagesBaseUrl ? { messagesBaseUrl: values.messagesBaseUrl } : {}),
        tenantName: values.tenantName,
        campaign: values.campaign,
        interactionTaskKey: values.interactionTaskKey,
      });
    } else if (target) {
      const patch: CcConnectorPatch = { expectedVersion: target.version };
      if (values.name !== target.name) patch.name = values.name;
      if (values.authBaseUrl !== target.authBaseUrl) patch.authBaseUrl = values.authBaseUrl;
      const incomingMsgUrl = values.messagesBaseUrl || '';
      const currentMsgUrl = target.messagesBaseUrl ?? '';
      if (incomingMsgUrl !== currentMsgUrl) patch.messagesBaseUrl = incomingMsgUrl;
      if (values.tenantName !== target.tenantName) patch.tenantName = values.tenantName;
      if (values.campaign !== target.campaign) patch.campaign = values.campaign;
      if (values.interactionTaskKey !== target.interactionTaskKey)
        patch.interactionTaskKey = values.interactionTaskKey;
      if (values.status !== target.status) patch.status = values.status;
      updateMutation.mutate(patch);
    }
  });

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded bg-white p-6 shadow-lg">
        <h3 className="mb-3 text-lg font-semibold">
          {mode === 'create' ? 'Nuevo Five9 connector' : `Editar ${target?.name}`}
        </h3>
        <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3">
          <Field label="Nombre" error={errors.name?.message}>
            <input className="w-full rounded border px-2 py-1 text-sm" {...register('name', { required: 'requerido', maxLength: 120 })} />
          </Field>
          <Field label="Status" error={errors.status?.message}>
            <select className="w-full rounded border px-2 py-1 text-sm" {...register('status')}>
              <option value="connected">connected</option>
              <option value="error">error</option>
            </select>
          </Field>
          <Field
            label="Auth Base URL (anon)"
            error={errors.authBaseUrl?.message}
            colSpan={2}
            hint="Ej. https://app.five9.com — Five9 hace POST aquí al endpoint /appsvcs/rs/svc/auth/anon"
          >
            <input
              className="w-full rounded border px-2 py-1 font-mono text-sm"
              placeholder="https://app.five9.com"
              {...register('authBaseUrl', { required: 'requerido', pattern: /^https:\/\// })}
            />
          </Field>
          <Field
            label="Messages Base URL (opcional)"
            error={errors.messagesBaseUrl?.message}
            colSpan={2}
            hint="Si está, override del apiHost devuelto por el auth. Útil cuando la farm es estable."
          >
            <input
              className="w-full rounded border px-2 py-1 font-mono text-sm"
              placeholder="https://app-atl.five9.com:443"
              {...register('messagesBaseUrl', { pattern: /^https:\/\/|^$/ })}
            />
          </Field>
          <Field label="Tenant Five9 (tenantName)" error={errors.tenantName?.message}>
            <input className="w-full rounded border px-2 py-1 text-sm" {...register('tenantName', { required: 'requerido' })} />
          </Field>
          <Field label="Campaign" error={errors.campaign?.message}>
            <input className="w-full rounded border px-2 py-1 text-sm" {...register('campaign', { required: 'requerido' })} />
          </Field>
          <Field label="interactionTaskKey" error={errors.interactionTaskKey?.message} colSpan={2}>
            <input
              className="w-full rounded border px-2 py-1 font-mono text-sm"
              {...register('interactionTaskKey', { required: 'requerido' })}
            />
          </Field>

          <div className="col-span-2 mt-3 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded border px-3 py-1 text-sm">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {mode === 'create' ? 'Crear' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  error,
  colSpan,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
  hint?: string;
}): JSX.Element {
  return (
    <label className={`flex flex-col ${colSpan === 2 ? 'col-span-2' : ''}`}>
      <span className="mb-1 text-xs font-medium text-slate-600">{label}</span>
      {children}
      {hint && <span className="mt-1 text-xs text-slate-500">{hint}</span>}
      {error && <span className="mt-1 text-xs text-red-700">{error}</span>}
    </label>
  );
}

function toMsg(e: unknown): string {
  return e instanceof ApiError ? e.message : (e as Error).message;
}
